import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import CommunitySelector from '@/components/CommunitySelector';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Shield, Eye, Mail, Calendar, Users, Bell, UserPlus, CalendarCheck, Trash2, AlertTriangle, MapPin, Fingerprint, BellOff, Pause, Play } from 'lucide-react';
import { startRegistration } from '@simplewebauthn/browser';
import usePushNotifications from '@/hooks/usePushNotifications';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';

const PrivacySettings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    ads_display: true,
    profile_visits_visible: true,
    who_can_message: 'all_members',
    contact_info_visibility: 'contacts_only',
    birthday_visibility: 'contacts_only',
    past_events_visibility: 'all_members',
  });
  const [emailSettings, setEmailSettings] = useState({
    connection_requests: true,
    connection_accepted: true,
    event_reminders: true,
    event_updates: true,
    weekly_digest: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1); // 1 = warning + acknowledgements, 2 = reason + type-DELETE
  const [ackPermanent, setAckPermanent] = useState(false);
  const [ackNoRestore, setAckNoRestore] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteDetails, setDeleteDetails] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [biometricCredentials, setBiometricCredentials] = useState([]);
  const [biometricLoading, setBiometricLoading] = useState(false);

  // Account pause (30-day cool-off)
  const [pauseStatus, setPauseStatus] = useState({ paused: false, days_left: 0, paused_until: null });
  const [pauseLoading, setPauseLoading] = useState(false);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: subscribeToPush, unsubscribe: unsubscribeFromPush } = usePushNotifications();
  const [pushTestStatus, setPushTestStatus] = useState(null);
  const [pushTesting, setPushTesting] = useState(false);

  useEffect(() => {
    if (user) {
      fetchSettings();
      fetchBiometricCredentials();
      fetchPauseStatus();
    }
  }, [user]);

  const fetchPauseStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/users/me/pause-status`);
      setPauseStatus(data);
    } catch {
      // silently fail; feature is optional
    }
  };

  const handlePauseAccount = async () => {
    setPauseLoading(true);
    try {
      const { data } = await axios.post(`${API}/users/me/pause`);
      setPauseStatus({ paused: true, paused_until: data.paused_until, days_left: data.days });
      setShowPauseDialog(false);
      toast.success(data.message || 'Your account has been paused for 30 days.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to pause account');
    } finally {
      setPauseLoading(false);
    }
  };

  const handleUnpauseAccount = async () => {
    setPauseLoading(true);
    try {
      const { data } = await axios.post(`${API}/users/me/unpause`);
      setPauseStatus({ paused: false, paused_until: null, days_left: 0 });
      toast.success(data.message || 'Welcome back — your account is active again.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unpause account');
    } finally {
      setPauseLoading(false);
    }
  };

  const fetchBiometricCredentials = async () => {
    try {
      const { data } = await axios.get(`${API}/webauthn/credentials`);
      setBiometricCredentials(data);
    } catch {
      // WebAuthn not available
    }
  };

  const handleEnableBiometric = async () => {
    setBiometricLoading(true);
    try {
      const { data: optionsJSON } = await axios.get(`${API}/webauthn/register/options`);
      const options = typeof optionsJSON === 'string' ? JSON.parse(optionsJSON) : optionsJSON;
      const credential = await startRegistration({ optionsJSON: options });
      await axios.post(`${API}/webauthn/register/verify`, credential);
      toast.success('Biometric login enabled!');
      fetchBiometricCredentials();
      // Clear the enrollment prompt dismissal so it doesn't show again
      localStorage.removeItem('biometric_prompt_dismissed');
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Biometric setup was cancelled');
      } else {
        toast.error(err?.response?.data?.detail || 'Failed to set up biometric login');
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleRemoveBiometric = async (credentialId) => {
    try {
      await axios.delete(`${API}/webauthn/credentials/${credentialId}`);
      toast.success('Biometric credential removed');
      fetchBiometricCredentials();
    } catch {
      toast.error('Failed to remove credential');
    }
  };

  const handleTogglePush = async () => {
    if (pushSubscribed) {
      // Unsubscribe using the hook (sets opt-out flag)
      const result = await unsubscribeFromPush();
      if (result) {
        toast.success('Push notifications disabled');
      } else {
        toast.error('Failed to disable push notifications');
      }
    } else {
      // Check if permission was previously denied
      if (Notification.permission === 'denied') {
        toast.error('Notifications are blocked by your browser', {
          description: 'To fix: tap the lock/info icon in your browser address bar → Site Settings → Notifications → Allow. Then try again.',
          duration: 12000
        });
        return;
      }
      // Subscribe
      const result = await subscribeToPush();
      if (result) {
        toast.success('Push notifications enabled');
      } else {
        toast.error('Could not enable notifications', {
          description: Notification.permission === 'denied' 
            ? 'Notifications are blocked. Tap the lock icon in your address bar → Site Settings → Notifications → Allow.'
            : 'Please try again. Make sure notifications are allowed in your browser settings.',
          duration: 10000
        });
      }
    }
  };

  const handleTestPush = async () => {
    setPushTesting(true);
    setPushTestStatus(null);
    try {
      // Re-sync current browser subscription to backend before testing
      let currentEndpoint = '';
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const subJson = sub.toJSON();
          currentEndpoint = subJson.endpoint || '';
          await axios.post(`${API}/push/subscribe`, {
            endpoint: subJson.endpoint,
            keys: subJson.keys
          });
        } else {
          setPushTestStatus('error');
          toast.error('No push subscription on this device. Toggle push OFF then ON.');
          setPushTesting(false);
          return;
        }
      } catch (syncErr) {
        console.error('Pre-test sync failed:', syncErr);
      }

      const res = await axios.post(`${API}/push/test?endpoint_hint=${encodeURIComponent(currentEndpoint.substring(0, 60))}`);
      if (res.data.success) {
        setPushTestStatus('sent');
      } else {
        const details = (res.data.details || []).join('; ').toLowerCase();
        const looksCorrupt =
          details.includes('invalid p256dh') ||
          details.includes('invalid ec key') ||
          details.includes('invalid auth') ||
          details.includes('valueerror');

        // Auto-recover: corrupt keys → force-rotate the browser subscription, then retry once
        if (looksCorrupt) {
          const recovered = await rotatePushSubscription();
          if (recovered) {
            const retry = await axios.post(`${API}/push/test`);
            if (retry.data.success) {
              setPushTestStatus('sent');
              toast.success('Push notifications fixed and test sent!');
              return;
            }
          }
        }

        setPushTestStatus('error');
        toast.error(res.data.error || 'Push test failed', {
          description: res.data.details ? res.data.details.join('; ') : undefined,
          duration: 8000
        });
      }
    } catch (e) {
      setPushTestStatus('error');
      toast.error(e.response?.data?.error || e.response?.data?.detail || 'Failed to send test push', {
        duration: 8000
      });
    } finally {
      setPushTesting(false);
    }
  };

  // Force-rotate: unsubscribe browser-side, then re-subscribe with current VAPID key.
  // Used both as a recovery path inside the test flow and as a manual "Reset" button.
  const rotatePushSubscription = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        try {
          const existingEndpoint = existing.toJSON().endpoint;
          await existing.unsubscribe();
          // Tell backend to drop the now-defunct endpoint too
          try {
            await axios.post(`${API}/push/unsubscribe-device`, { endpoint: existingEndpoint });
          } catch {}
        } catch (e) {
          console.warn('Browser-side unsubscribe failed:', e);
        }
      }
      // Re-subscribe via the hook so opt-out flags + backend sync are handled centrally
      const ok = await subscribeToPush();
      return !!ok;
    } catch (e) {
      console.error('rotatePushSubscription failed:', e);
      return false;
    }
  };

  const handleResetPush = async () => {
    setPushTesting(true);
    setPushTestStatus(null);
    const ok = await rotatePushSubscription();
    setPushTesting(false);
    if (ok) {
      toast.success('Push notifications reset. Sending a test push…');
      // Give the backend a beat to persist the new subscription
      setTimeout(handleTestPush, 800);
    } else {
      toast.error('Could not reset push notifications. Check browser permission and try again.');
    }
  };

  const fetchSettings = async () => {
    try {
      const [privacyResponse, emailResponse] = await Promise.all([
        axios.get(`${API}/users/me/privacy`),
        axios.get(`${API}/users/me/email-notifications`)
      ]);
      setSettings(privacyResponse.data);
      setEmailSettings(emailResponse.data);
    } catch (error) {
      console.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/users/me/privacy`, settings),
        axios.put(`${API}/users/me/email-notifications`, emailSettings)
      ]);
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    if (!deleteReason) {
      toast.error('Please select a reason for leaving');
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/me`, {
        data: { reason: deleteReason, reason_details: deleteDetails }
      });
      toast.success('Your account has been deleted');
      logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-8">
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">Privacy Settings</h1>
              <p className="text-lg text-muted-foreground">
                Control who can see your information and how you interact with others
              </p>
            </div>

            <div className="space-y-6">
              {/* Community Selector */}
              <CommunitySelector user={user} variant="settings" />
              
              {/* Ads Display */}
              <div className="bg-white rounded-3xl p-8 border-2 border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Eye className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-bold mb-2">Advertisement Display</h3>
                      <p className="text-muted-foreground">
                        Control whether ads are shown to you on the platform
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.ads_display}
                    onCheckedChange={(checked) => setSettings({ ...settings, ads_display: checked })}
                  />
                </div>
              </div>

              {/* Profile Visits */}
              <div className="bg-white rounded-3xl p-8 border-2 border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-bold mb-2">Profile Visit Visibility</h3>
                      <p className="text-muted-foreground">
                        Let others know when you visit their profile
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.profile_visits_visible}
                    onCheckedChange={(checked) => setSettings({ ...settings, profile_visits_visible: checked })}
                  />
                </div>
              </div>

              {/* Who Can Message */}
              <div className="bg-white rounded-3xl p-8 border-2 border-border">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-heading font-bold mb-2">Who Can Send Messages</h3>
                    <p className="text-muted-foreground mb-4">
                      Control who can send you direct messages
                    </p>
                    <Select
                      value={settings.who_can_message}
                      onValueChange={(value) => setSettings({ ...settings, who_can_message: value })}
                    >
                      <SelectTrigger className="h-12 rounded-xl border-2 max-w-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_members">All Members</SelectItem>
                        <SelectItem value="contacts_only">Contacts Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Muted Conversations */}
              <MutedConversationsCard />

              {/* Contact Info Visibility */}
              <div className="bg-white rounded-3xl p-8 border-2 border-border">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-heading font-bold mb-2">Contact Information Visibility</h3>
                    <p className="text-muted-foreground mb-4">
                      Control who can see your email and contact details
                    </p>
                    <Select
                      value={settings.contact_info_visibility}
                      onValueChange={(value) => setSettings({ ...settings, contact_info_visibility: value })}
                    >
                      <SelectTrigger className="h-12 rounded-xl border-2 max-w-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_members">All Members</SelectItem>
                        <SelectItem value="contacts_only">Contacts Only</SelectItem>
                        <SelectItem value="no_one">No One</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Birthday Visibility */}
              <div className="bg-white rounded-3xl p-8 border-2 border-border">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-heading font-bold mb-2">Birthday Visibility</h3>
                    <p className="text-muted-foreground mb-4">
                      Control who can see your birthday
                    </p>
                    <Select
                      value={settings.birthday_visibility}
                      onValueChange={(value) => setSettings({ ...settings, birthday_visibility: value })}
                    >
                      <SelectTrigger className="h-12 rounded-xl border-2 max-w-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_members">All Members</SelectItem>
                        <SelectItem value="contacts_only">Contacts Only</SelectItem>
                        <SelectItem value="no_one">No One</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Past Events Visibility */}
              <div className="bg-white rounded-3xl p-8 border-2 border-border">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-heading font-bold mb-2">Past Events Attended</h3>
                    <p className="text-muted-foreground mb-4">
                      Control who can see which events you've attended
                    </p>
                    <Select
                      value={settings.past_events_visibility}
                      onValueChange={(value) => setSettings({ ...settings, past_events_visibility: value })}
                    >
                      <SelectTrigger className="h-12 rounded-xl border-2 max-w-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_members">All Members</SelectItem>
                        <SelectItem value="contacts_only">Contacts Only</SelectItem>
                        <SelectItem value="no_one">No One</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Email Notifications Section */}
              <div className="mt-12 mb-8">
                <h2 className="text-3xl font-heading font-bold mb-2">Email Notifications</h2>
                <p className="text-lg text-muted-foreground">
                  Choose which email notifications you'd like to receive
                </p>
              </div>

              {/* Connection Request Notifications */}
              <div className="bg-white rounded-3xl p-8 border-2 border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <UserPlus className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-bold mb-2">Connection Requests</h3>
                      <p className="text-muted-foreground">
                        Get notified when someone sends you a connection request
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={emailSettings.connection_requests}
                    onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, connection_requests: checked })}
                  />
                </div>
              </div>

              {/* Connection Accepted Notifications */}
              <div className="bg-white rounded-3xl p-8 border-2 border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-bold mb-2">Connection Accepted</h3>
                      <p className="text-muted-foreground">
                        Get notified when someone accepts your connection request
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={emailSettings.connection_accepted}
                    onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, connection_accepted: checked })}
                  />
                </div>
              </div>

              {/* Event Reminders */}
              <div className="bg-white rounded-3xl p-8 border-2 border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <CalendarCheck className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-bold mb-2">Event Reminders</h3>
                      <p className="text-muted-foreground">
                        Receive reminders about upcoming events you're attending
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={emailSettings.event_reminders}
                    onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, event_reminders: checked })}
                  />
                </div>
              </div>

              {/* Event Updates */}
              <div className="bg-white rounded-3xl p-8 border-2 border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Bell className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-bold mb-2">Event Updates</h3>
                      <p className="text-muted-foreground">
                        Get notified about changes to events you're attending
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={emailSettings.event_updates}
                    onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, event_updates: checked })}
                  />
                </div>
              </div>

              {/* Weekly Digest */}
              <div className="bg-white rounded-3xl p-8 border-2 border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-bold mb-2" data-testid="weekly-digest-label">Weekly Digest</h3>
                      <p className="text-muted-foreground">
                        Receive a Sunday summary of events, groups, and connections in your city
                      </p>
                    </div>
                  </div>
                  <Switch
                    data-testid="weekly-digest-toggle"
                    checked={emailSettings.weekly_digest}
                    onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, weekly_digest: checked })}
                  />
                </div>
              </div>

              {/* Push Notifications Section */}
              {pushSupported && (
                <>
                  <div className="mt-12 mb-8">
                    <h2 className="text-3xl font-heading font-bold mb-2">Push Notifications</h2>
                    <p className="text-lg text-muted-foreground">
                      Get instant alerts on your device for messages, events, and connections
                    </p>
                  </div>

                  <div className="bg-white rounded-3xl p-8 border-2 border-border" data-testid="push-notification-settings">
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Bell className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-xl font-heading font-bold mb-2">Push Notifications</h3>
                          <p className="text-muted-foreground">
                            {pushSubscribed 
                              ? 'Push notifications are enabled on this device'
                              : 'Enable to receive instant alerts on your device'
                            }
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={pushSubscribed}
                        onCheckedChange={handleTogglePush}
                        data-testid="push-toggle"
                      />
                    </div>

                    {/* Blocked permission banner — environment-aware (PWA vs browser, iOS vs Android, TWA-aware) */}
                    {!pushSubscribed && typeof Notification !== 'undefined' && Notification.permission === 'denied' && (() => {
                      const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
                      const isIOSDev = /iPad|iPhone|iPod/.test(ua);
                      const isStandalone = typeof window !== 'undefined' && (
                        window.matchMedia?.('(display-mode: standalone)').matches ||
                        window.navigator.standalone === true
                      );
                      // TWA detection: document.referrer is set to "android-app://..." when launched
                      // from a Trusted Web Activity (e.g. Play Store-installed PWA wrapper).
                      const isTWA = typeof document !== 'undefined' &&
                        document.referrer.startsWith('android-app://');
                      const isAndroidApp = isStandalone && !isIOSDev;

                      let title;
                      let intro;
                      let steps;
                      if (isTWA || isAndroidApp) {
                        title = 'Notifications are turned off for the ExpatsSphere app';
                        intro = isTWA
                          ? "You installed ExpatsSphere from Google Play, so notifications are controlled by the Android app's notification permission (not Chrome). Re-enable from your phone's Settings:"
                          : "You installed ExpatsSphere as a home-screen app, so notifications are controlled by the Android app's notification permission (not Chrome). Re-enable from your phone's Settings:";
                        steps = [
                          'Open your phone\'s Settings app (the grey gear icon — not Chrome)',
                          'Tap Apps (or "Apps & notifications" on some phones)',
                          'Find and tap ExpatsSphere',
                          'Tap Notifications',
                          'Toggle "Show notifications" ON',
                          'Return here, toggle Push Notifications ON above — the "Send test" button will appear'
                        ];
                      } else if (isIOSDev && isStandalone) {
                        title = 'Notifications are blocked at the iOS app level';
                        intro = 'Re-enable from the iPhone Settings app:';
                        steps = [
                          'Open the iPhone Settings app',
                          'Scroll down and tap ExpatsSphere',
                          'Tap Notifications → Allow Notifications ON',
                          'Return here — the Send test button will appear'
                        ];
                      } else if (isIOSDev) {
                        title = 'Notifications are blocked in Safari';
                        intro = 'Re-enable from Safari\'s site settings:';
                        steps = [
                          'Tap the AA icon in the Safari address bar',
                          'Choose Website Settings → Notifications → Allow',
                          'Reload this page, then toggle Push Notifications ON'
                        ];
                      } else {
                        title = 'Notifications are blocked in your browser';
                        intro = 'Re-enable from Chrome\'s site settings:';
                        steps = [
                          'Tap the ⋮ (3-dot) menu in the Chrome top-right',
                          'Open Settings → Site settings → Notifications',
                          'Find expatssphere.com and set it to Allow',
                          'Return here and toggle Push Notifications ON'
                        ];
                      }

                      return (
                        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl space-y-3" data-testid="push-blocked-banner">
                          <p className="text-sm font-semibold text-red-700">{title}</p>
                          <p className="text-xs text-red-700">{intro}</p>
                          <ol className="text-xs text-red-700 list-decimal list-inside space-y-1">
                            {steps.map((s, i) => <li key={i}>{s}</li>)}
                          </ol>
                          <p className="text-xs text-red-600 italic">
                            Once allowed, the <strong>Send test</strong> button below will reappear automatically.
                          </p>
                        </div>
                      );
                    })()}

                    {/* Default (not subscribed, not blocked) — hint */}
                    {!pushSubscribed && typeof Notification !== 'undefined' && Notification.permission !== 'denied' && (
                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                        <p className="text-xs text-blue-700">
                          Toggle the switch above to enable push notifications on this device. The "Send test" button will appear once enabled.
                        </p>
                      </div>
                    )}

                    {pushSubscribed && (
                      <div className="mt-4 pt-4 border-t border-border flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-sm text-muted-foreground flex-1 min-w-[180px]">
                          {pushTestStatus === 'sent' 
                            ? 'Test notification sent! Check your device.'
                            : pushTestStatus === 'error'
                            ? 'Push test failed — tap Reset to refresh your subscription.'
                            : 'Tap to send a test notification to this device'
                          }
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleResetPush}
                            disabled={pushTesting}
                            data-testid="reset-push-btn"
                            title="Unsubscribe and re-subscribe to fix stale push keys"
                          >
                            Reset
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleTestPush}
                            disabled={pushTesting}
                            data-testid="test-push-btn"
                          >
                            {pushTesting ? 'Sending...' : pushTestStatus === 'sent' ? 'Sent!' : 'Send test'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Save Button */}
              <div className="flex justify-end pt-6">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-full font-bold brutal-shadow"
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>

              {/* Biometric Login Section */}
              <div className="mt-12 mb-8">
                <h2 className="text-3xl font-heading font-bold mb-2">Security</h2>
                <p className="text-lg text-muted-foreground">
                  Manage how you sign in to your account
                </p>
              </div>

              <div className="bg-white rounded-3xl p-8 border-2 border-border" data-testid="biometric-settings">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Fingerprint className="w-6 h-6 text-violet-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-heading font-bold mb-2">Biometric Login</h3>
                    <p className="text-muted-foreground mb-4">
                      Skip your password and log in instantly with your face or fingerprint.
                    </p>

                    {biometricCredentials.length > 0 ? (
                      <div className="space-y-3">
                        {biometricCredentials.map((cred, i) => (
                          <div key={cred.credential_id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-xl">
                            <div className="flex items-center gap-2">
                              <Fingerprint className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-medium text-green-800">
                                Device {i + 1} — Enabled
                              </span>
                              <span className="text-xs text-green-600">
                                {cred.created_at ? new Date(cred.created_at).toLocaleDateString() : ''}
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRemoveBiometric(cred.credential_id)}
                              data-testid={`remove-biometric-${i}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Button
                        className="bg-violet-600 hover:bg-violet-700 text-white"
                        onClick={handleEnableBiometric}
                        disabled={biometricLoading}
                        data-testid="enable-biometric-btn"
                      >
                        <Fingerprint className="w-4 h-4 mr-2" />
                        {biometricLoading ? 'Setting up...' : 'Enable Biometric Login'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Delete Account Section */}
              <div className="mt-12 mb-8">
                <h2 className="text-3xl font-heading font-bold mb-2 text-destructive">Danger Zone</h2>
                <p className="text-lg text-muted-foreground">
                  Irreversible actions that affect your account
                </p>
              </div>

              {/* Pause Account (softer alternative to deletion) */}
              <div
                className={`rounded-3xl p-8 border-2 mb-6 ${pauseStatus.paused ? 'bg-amber-50 border-amber-300' : 'bg-blue-50 border-blue-200'}`}
                data-testid="pause-account-card"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${pauseStatus.paused ? 'bg-amber-100' : 'bg-blue-100'}`}>
                    {pauseStatus.paused ? (
                      <Play className="w-6 h-6 text-amber-600" />
                    ) : (
                      <Pause className="w-6 h-6 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    {pauseStatus.paused ? (
                      <>
                        <h3 className="text-xl font-heading font-bold mb-2 text-amber-800" data-testid="pause-status-heading">
                          Your account is paused
                        </h3>
                        <p className="text-amber-800 mb-4">
                          You're taking a break. Your profile is hidden from search and Find Expats,
                          push notifications are silenced, and content emails are paused.
                          <br />
                          <strong>{pauseStatus.days_left}</strong>{' '}
                          {pauseStatus.days_left === 1 ? 'day' : 'days'} left — auto-resumes on{' '}
                          <strong>
                            {pauseStatus.paused_until
                              ? new Date(pauseStatus.paused_until).toLocaleDateString(undefined, {
                                  year: 'numeric', month: 'long', day: 'numeric',
                                })
                              : ''}
                          </strong>.
                          You can come back any time.
                        </p>
                        <Button
                          className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                          disabled={pauseLoading}
                          onClick={handleUnpauseAccount}
                          data-testid="unpause-account-btn"
                        >
                          <Play className="w-4 h-4 mr-2" />
                          {pauseLoading ? 'Unpausing…' : 'Unpause Now'}
                        </Button>
                      </>
                    ) : (
                      <>
                        <h3 className="text-xl font-heading font-bold mb-2 text-blue-800">
                          Pause My Account — 30 Days
                        </h3>
                        <p className="text-blue-800 mb-4">
                          Not ready to delete? Take a 30-day break instead. Your profile is hidden from
                          search and Find Expats, push notifications are silenced, and content emails
                          are paused. Nothing is deleted. You can come back and unpause at any time,
                          and your account auto-resumes after 30 days.
                        </p>
                        <Button
                          variant="outline"
                          className="rounded-full border-blue-300 text-blue-700 hover:bg-blue-100"
                          disabled={pauseLoading}
                          onClick={() => setShowPauseDialog(true)}
                          data-testid="pause-account-btn"
                        >
                          <Pause className="w-4 h-4 mr-2" />
                          Pause My Account
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-red-50 rounded-3xl p-8 border-2 border-red-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Trash2 className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-heading font-bold mb-2 text-red-700">Delete Account</h3>
                    <p className="text-red-600 mb-4">
                      Permanently delete your account and all associated data. Deletion is immediate — there is no restore window and no backup retention. You'll be walked through a 2-step confirmation before anything is deleted.
                    </p>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        setDeleteStep(1);
                        setAckPermanent(false);
                        setAckNoRestore(false);
                        setDeleteConfirmText('');
                        setDeleteReason('');
                        setDeleteDetails('');
                        setShowDeleteDialog(true);
                      }}
                      className="rounded-full"
                      data-testid="delete-account-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete My Account
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Pause Account Dialog */}
      <Dialog open={showPauseDialog} onOpenChange={setShowPauseDialog}>
        <DialogContent className="max-w-md" data-testid="pause-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Pause className="w-5 h-5" />
              Pause My Account — 30 Days
            </DialogTitle>
            <DialogDescription className="text-left">
              A softer alternative to deletion. Nothing is deleted — everything comes back when you unpause.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 mb-2">
                <strong>While paused, for the next 30 days:</strong>
              </p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Your profile is hidden from Search and Find Expats</li>
                <li>• Push notifications are silenced</li>
                <li>• Content emails are paused (event reminders, digests, messages)</li>
                <li>• System emails still work (password reset, email verification)</li>
                <li>• You can still log in and unpause any time</li>
                <li>• The account auto-resumes after 30 days — no action required</li>
              </ul>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowPauseDialog(false)}
                className="flex-1"
                disabled={pauseLoading}
                data-testid="pause-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handlePauseAccount}
                disabled={pauseLoading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="confirm-pause-btn"
              >
                <Pause className="w-4 h-4 mr-2" />
                {pauseLoading ? 'Pausing…' : 'Pause for 30 Days'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog (2-step) */}
      <Dialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open) {
            setDeleteStep(1);
            setAckPermanent(false);
            setAckNoRestore(false);
            setDeleteConfirmText('');
            setDeleteReason('');
            setDeleteDetails('');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {deleteStep === 1 ? 'Delete Account — Step 1 of 2' : 'Delete Account — Step 2 of 2'}
            </DialogTitle>
            <DialogDescription className="text-left">
              {deleteStep === 1
                ? 'Please read what will happen. Account deletion is immediate and permanent.'
                : 'Tell us why you\'re leaving and type DELETE to confirm.'}
            </DialogDescription>
          </DialogHeader>

          {deleteStep === 1 && (
            <div className="space-y-4 py-4" data-testid="delete-step-1">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700 mb-2">
                  <strong>These will be permanently deleted in one transaction:</strong>
                </p>
                <ul className="text-sm text-red-600 space-y-1">
                  <li>• Your user record and profile</li>
                  <li>• Events you created — <em>not</em> placed in the 30-day event trash</li>
                  <li>• Jobs, properties, marketplace items, and business listings you created</li>
                  <li>• Messages and conversations (including copies visible to other participants)</li>
                  <li>• Push subscriptions and profile-view records</li>
                  <li>• Reports you submitted and reports about you</li>
                  <li>• Your contacts and connections, including entries on other members' profiles</li>
                </ul>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                Other members may have copied information outside ExpatsSphere before you delete. We cannot delete those outside copies.
              </div>

              <label className="flex items-start gap-3 cursor-pointer" data-testid="ack-permanent-label">
                <input
                  type="checkbox"
                  checked={ackPermanent}
                  onChange={(e) => setAckPermanent(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-red-600 cursor-pointer"
                  data-testid="ack-permanent-checkbox"
                />
                <span className="text-sm text-gray-800">
                  I understand my account and all listed data will be <strong>permanently deleted</strong>.
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer" data-testid="ack-no-restore-label">
                <input
                  type="checkbox"
                  checked={ackNoRestore}
                  onChange={(e) => setAckNoRestore(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-red-600 cursor-pointer"
                  data-testid="ack-no-restore-checkbox"
                />
                <span className="text-sm text-gray-800">
                  I understand there is <strong>no restore window</strong> and <strong>no backup retention</strong>.
                </span>
              </label>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteDialog(false)}
                  className="flex-1"
                  data-testid="delete-cancel-step1-btn"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setDeleteStep(2)}
                  disabled={!ackPermanent || !ackNoRestore}
                  className="flex-1"
                  data-testid="delete-continue-step2-btn"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {deleteStep === 2 && (
            <div className="space-y-4 py-4" data-testid="delete-step-2">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Why are you leaving?
                </label>
                <select
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="w-full rounded-lg border border-red-200 px-3 py-2 text-sm focus:border-red-400 focus:outline-none"
                  data-testid="delete-reason-select"
                >
                  <option value="">Select a reason...</option>
                  <option value="Not enough events in my area">Not enough events in my area</option>
                  <option value="Found another community">Found another community</option>
                  <option value="Privacy concerns">Privacy concerns</option>
                  <option value="Moving back home">Moving back home</option>
                  <option value="Too many notifications">Too many notifications</option>
                  <option value="Not using the app enough">Not using the app enough</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Any additional feedback? <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={deleteDetails}
                  onChange={(e) => setDeleteDetails(e.target.value)}
                  placeholder="Help us improve — tell us more..."
                  className="w-full rounded-lg border border-red-200 px-3 py-2 text-sm focus:border-red-400 focus:outline-none resize-none"
                  rows={3}
                  data-testid="delete-reason-details"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Type <span className="font-bold text-red-600">DELETE</span> to confirm:
                </label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="mt-2 border-red-200 focus:border-red-400"
                  data-testid="delete-confirm-input"
                  autoFocus
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteStep(1)}
                  className="flex-1"
                  data-testid="delete-back-step1-btn"
                >
                  Back
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteAccount}
                  disabled={deleting || deleteConfirmText !== 'DELETE' || !deleteReason}
                  className="flex-1"
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? 'Deleting...' : 'Delete Forever'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrivacySettings;

// ---------------------------------------------------------------------------
// MutedConversationsCard: lists every user the viewer has silenced from
// pushing them message notifications, with one-tap un-mute per row.
// ---------------------------------------------------------------------------
function MutedConversationsCard() {
  const [muted, setMuted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unmuting, setUnmuting] = useState(null);

  const fetchMuted = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/messages/mute`);
      setMuted(r.data?.muted || []);
    } catch {
      setMuted([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMuted();
  }, []);

  const handleUnmute = async (userId) => {
    setUnmuting(userId);
    try {
      await axios.delete(`${API}/messages/mute/${userId}`);
      setMuted((prev) => prev.filter((u) => u.user_id !== userId));
      toast.success('Conversation un-muted');
    } catch (e) {
      toast.error('Could not un-mute');
    } finally {
      setUnmuting(null);
    }
  };

  return (
    <div className="bg-white rounded-3xl p-8 border-2 border-border" data-testid="muted-conversations-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
          <BellOff className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-heading font-bold mb-2">Muted Conversations</h3>
          <p className="text-muted-foreground mb-4">
            Threads here won't trigger push or in-app notifications. The conversation
            itself stays visible — only the noise is silenced.
          </p>

          {loading && (
            <p className="text-sm text-muted-foreground py-2">Loading…</p>
          )}

          {!loading && muted.length === 0 && (
            <p
              className="text-sm text-muted-foreground py-2"
              data-testid="muted-empty-state"
            >
              No muted conversations. Tap "🔕 Mute" on any new-message push to silence
              a noisy thread instantly.
            </p>
          )}

          {!loading && muted.length > 0 && (
            <ul className="divide-y divide-gray-100" data-testid="muted-list">
              {muted.map((u) => (
                <li
                  key={u.user_id}
                  className="flex items-center gap-3 py-3"
                  data-testid={`muted-row-${u.user_id}`}
                >
                  {u.profile_picture ? (
                    <img
                      src={u.profile_picture}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-semibold text-gray-600 flex-shrink-0">
                      {(u.name || '?')[0]}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{u.name || 'Unknown user'}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUnmute(u.user_id)}
                    disabled={unmuting === u.user_id}
                    className="rounded-full flex-shrink-0"
                    data-testid={`unmute-btn-${u.user_id}`}
                  >
                    {unmuting === u.user_id ? 'Un-muting…' : 'Un-mute'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

